import { useEffect, useCallback, useState, useMemo } from 'react';
import { useNavigate, generatePath, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SelectItemProps,
  GeneralLedgerSidepanel,
  ajvResolver,
  AccountType,
  Account,
  GeneralLedgerTransactionFormValues,
  GeneralLedgerTransaction as GeneralLedgerTransactionType,
  ResponseError,
  responseErrorsResolver,
  Response,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  clearAccountBalance,
  clearTransactionFromFeeState,
} from '../store/slices/tariffs/transaction-from-fee';
import { CustomerType } from '../types';
import {
  useCreateCustomersGLTransactionMutation,
  useGetCustomersAccountsCategoriesQuery,
  useLazyGetCustomersAccountBalanceByIdQuery,
  useLazyGetCustomersAccountsListQuery,
  useLazyGetCustomersBaseCurrencyQuery,
  useLazyGetCustomerAllCreditAccountBalancesQuery,
  useLazyGetCustomerAllDebitAccountBalancesQuery,
} from '../store/services/customers';
import ROUTES from '../../../routes';

const schema = {
  type: 'object',
  properties: {
    transferDate: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Date is required',
    },
    sum: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Amount is required',
    },
    currencyId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Currency is required',
    },
    debitAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Debit account is required',
    },
    creditAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Credit account is required',
    },
    info: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Info is required',
    },
  },
  required: [
    'transferDate',
    'sum',
    'currencyId',
    'debitAccountNumber',
    'creditAccountNumber',
    'info',
  ],
};

const TransactionFromFeeHOC = ({
  customerType,
}: {
  customerType: CustomerType;
}) => {
  const { customerId, feeId } = useParams();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  let link: string;
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [creditAccounts, setCreditAccounts] = useState<Account[]>([]);
  const [globalError, setGlobalError] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState<
    SelectItemProps[]
  >([]);

  const {
    customersDictionaries: {
      currencies,
      currenciesList: dictionariesCurrenciesList,
      baseCurrency,
    },
    customerTransactionFromFee: {
      baseCurrencyId,
      creditAccountBalance,
      debitAccountBalance,
      accountCategories,
      debitBalances,
      creditBalances,
    },
    individualCustomers: {
      info: {
        general: {
          baseInformation: individualBaseInformation,
          baseInformationIsLoading: individualBaseInformationIsLoading,
        },
      },
    },
    corporateCustomers: {
      info: {
        general: {
          baseInformation: corporateBaseInformation,
          baseInformationIsLoading: corporateBaseInformationIsLoading,
        },
      },
    },
  } = useAppSelector((state) => state);

  let info = individualBaseInformation;

  if (customerType === CustomerType.CORPORATE) {
    info = corporateBaseInformation;
  }

  let debitAccounts: Account[] = [];
  let debitAccountsIsLoading;

  if (customerType === CustomerType.INDIVIDUAL) {
    link = ROUTES.CUSTOMERS_INDIVIDUAL_TARIFFS;
    debitAccounts = individualBaseInformation?.accounts || [];
    debitAccountsIsLoading = individualBaseInformationIsLoading;
  } else {
    link = ROUTES.CUSTOMERS_CORPORATE_TARIFFS;
    debitAccounts = corporateBaseInformation?.accounts || [];
    debitAccountsIsLoading = corporateBaseInformationIsLoading;
  }

  const debitAccountsList: SelectItemProps[] = useMemo(
    () =>
      debitAccounts.map((item) => ({
        name: item.description,
        suffix: item.fullNumber,
        value: item.fullNumber,
      })),
    [debitAccounts],
  );

  const {
    control,
    setError,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: ajvResolver(schema),
  });

  const fields = watch();
  const currencyId = watch('currencyId');
  const debitAccountNumber = watch('debitAccountNumber');
  const creditAccountNumber = watch('creditAccountNumber');

  const [getCustomersBaseCurrency, { isLoading: baseCurrencyIsLoading }] =
    useLazyGetCustomersBaseCurrencyQuery();

  const [create, { isLoading: createIsLoading, isSuccess: createIsSuccess }] =
    useCreateCustomersGLTransactionMutation();

  const [
    getDebitAccountBalanceById,
    { isLoading: debitBalanceIsLoading, isFetching: debitBalanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceByIdQuery();
  const [
    getCreditAccountBalanceById,
    { isLoading: creditBalanceIsLoading, isFetching: creditBalanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceByIdQuery();

  const { isLoading: accountsCategoriesLoading } =
    useGetCustomersAccountsCategoriesQuery('all=true&include=primaryAccounts');

  const [
    getCustomerAllDebitAccountBalances,
    {
      isLoading: allDebitAccountBalancesIsLoading,
      isFetching: allDebitAccountBalancesisFetching,
    },
  ] = useLazyGetCustomerAllDebitAccountBalancesQuery();

  const [
    getCustomerAllCreditAccountBalances,
    {
      isLoading: allCreditAccountBalancesIsLoading,
      isFetching: allCreditAccountBalancesisFetching,
    },
  ] = useLazyGetCustomerAllCreditAccountBalancesQuery();

  const [
    getCreditAccountsList,
    {
      isLoading: creditAccountsListLoading,
      isFetching: creditAccountsListUpdateInProgress,
    },
  ] = useLazyGetCustomersAccountsListQuery();

  const getCustomerAllDebitAccountBalancesAction = useCallback((id: string) => {
    getCustomerAllDebitAccountBalances({
      id,
    });
  }, []);

  const getCustomerAllCreditAccountBalancesAction = useCallback(
    (id: string) => {
      getCustomerAllCreditAccountBalances({
        id,
      });
    },
    [],
  );

  const getCreditAccountsListAction = useCallback(async (query: string) => {
    if (query && query.length) {
      const response = await getCreditAccountsList(
        `?fullNumber=4.0.&${query}&all=true`,
      );

      if (response && response.data) {
        const filteredRes = response.data.data.edges
          .map((item) => item.node)
          .filter((item) => item.fullNumber !== debitAccountNumber);

        setCreditAccounts(filteredRes);
      }
    }
  }, []);

  const handleClose = () => {
    setPanelIsOpen(false);
    dispatch(clearTransactionFromFeeState());

    setTimeout(() => {
      navigate(
        generatePath(link, {
          customerId: customerId as string,
        }),
      );
    }, 100);
  };

  const onSearch = async (name: string, searchText: string) => {
    const results: Account[] = [];

    if (searchText) {
      if (name.toLowerCase().indexOf('credit') >= 0) {
        const response = (await getCreditAccountsList(
          `?fullNumber=4.0.&search=${searchText}&all=true`,
        )) as { data: Response<Account> };

        if (response?.data?.data) {
          response.data.data.edges?.forEach((item) => {
            results.push(item.node);
          });
        }

        const filteredRes = results.filter(
          (item) => item.fullNumber !== debitAccountNumber,
        );

        setCreditAccounts(filteredRes);
      }
    }
  };

  const onUpdateBalance = (
    type: AccountType.DEBIT | AccountType.CREDIT,
    accountId: string,
  ) => {
    let query = '';

    if (currencyId) {
      query = `&currencyId=${currencyId}`;
    } else if (baseCurrencyId) {
      query = `&currencyId=${baseCurrencyId}`;
    }

    if (accountId) {
      if (type === AccountType.DEBIT) {
        getDebitAccountBalanceById({
          id: accountId,
          query,
          typeAcc: type,
        });
      } else if (type === AccountType.CREDIT) {
        getCreditAccountBalanceById({
          id: accountId,
          query,
          typeAcc: type,
        });
      }
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body = {
      ...values,
      sum: Number(values.sum),
      documentNumber: values.documentNumber || undefined,
      info: values.info || undefined,
    };

    const response = (await create(
      body as GeneralLedgerTransactionFormValues,
    )) as {
      data?: GeneralLedgerTransactionType;
      error: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  useEffect(() => {
    setPanelIsOpen(true);
    getCustomersBaseCurrency();

    return () => {
      dispatch(clearTransactionFromFeeState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (debitBalances && creditBalances && currencies && baseCurrency) {
      const currenciesList: SelectItemProps[] = [];

      if (
        debitBalances.find((item) => item.currencyId === baseCurrency.id) &&
        creditBalances?.find((item) => item.currencyId === baseCurrency.id)
      ) {
        currenciesList.push({
          name: baseCurrency.name,
          value: baseCurrency.id,
          suffix: baseCurrency?.description,
        });
      }

      currencies
        ?.filter((item) => item.value !== baseCurrency?.id)
        .forEach((item) => {
          if (
            debitBalances?.find(
              (balance) => balance.currencyId === item.value,
            ) &&
            creditBalances?.find((balance) => balance.currencyId === item.value)
          ) {
            currenciesList.push(item);
          }
        });

      setAvailableCurrencies(currenciesList);
    }
  }, [debitBalances, creditBalances, currencies, baseCurrency]);

  useEffect(() => {
    if (baseCurrencyId) {
      reset({
        currencyId: baseCurrencyId,
        transferDate: new Date().toISOString(),
        debitAccountNumber: debitAccountsList[0]?.value || undefined,
        debitAccountId: debitAccounts[0]?.id || undefined,
      });
    }
  }, [dispatch, baseCurrencyId, debitAccountsList]);

  useEffect(() => {
    if (createIsSuccess) {
      navigate(
        generatePath(link, {
          customerId: customerId as string,
        }),
      );
    }
  }, [createIsSuccess]);

  useEffect(() => {
    if (debitAccountNumber && debitAccounts) {
      const debitAccountId = debitAccounts?.find(
        (item) => item.fullNumber === debitAccountNumber,
      )?.id;
      setValue('debitAccountId', debitAccountId);
      onUpdateBalance(AccountType.DEBIT, debitAccountId || '');

      if (debitAccountId) {
        getCustomerAllDebitAccountBalancesAction(debitAccountId);
      }
    } else {
      setValue('debitAccountId', undefined);
      dispatch(clearAccountBalance(AccountType.DEBIT));
    }
  }, [debitAccountNumber, debitAccounts]);

  useEffect(() => {
    if (creditAccountNumber && creditAccounts) {
      const creditAccountId = creditAccounts?.find(
        (item) => item.fullNumber === creditAccountNumber,
      )?.id;
      setValue('creditAccountId', creditAccountId);
      onUpdateBalance(AccountType.CREDIT, creditAccountId || '');

      if (creditAccountId) {
        getCustomerAllCreditAccountBalancesAction(creditAccountId);
      }
    } else {
      setValue('creditAccountId', undefined);
      dispatch(clearAccountBalance(AccountType.CREDIT));
    }
  }, [creditAccountNumber, creditAccounts]);

  useEffect(() => {
    if (info?.fees) {
      const currentFeeItem = info?.fees?.find((item) => item.id === feeId);

      if (currentFeeItem) {
        reset({
          ...fields,
          creditAccountNumber: currentFeeItem.fee.feeTargetAccountNumber,
          documentNumber: `${currentFeeItem.name} ${currentFeeItem.fee.id}`,
          sum:
            currentFeeItem.feeFixedAmount >= 0
              ? currentFeeItem.feeFixedAmount?.toString()
              : undefined,
        });
      }
    }
  }, [info?.fees]);

  useEffect(() => {
    if (currencyId) {
      if (fields.debitAccountId) {
        onUpdateBalance(AccountType.DEBIT, fields.debitAccountId);
      }
      if (fields.creditAccountId) {
        onUpdateBalance(AccountType.CREDIT, fields.creditAccountId);
      }
    }
  }, [currencyId, fields.creditAccountId, fields.debitAccountId]);

  return (
    <GeneralLedgerSidepanel
      panelIsOpen={panelIsOpen}
      handleClose={handleClose}
      title="Write-off funds"
      okButtonText="Write-off"
      createIsLoading={createIsLoading}
      isLoading={debitAccountsIsLoading || baseCurrencyIsLoading}
      control={control}
      onSubmit={onSubmit}
      currencies={availableCurrencies}
      currenciesList={dictionariesCurrenciesList}
      debitAccounts={debitAccountsList}
      creditAccounts={creditAccounts}
      creditIsLoading={
        creditAccountsListLoading || creditAccountsListUpdateInProgress
      }
      debitBalanceIsLoading={debitBalanceIsLoading || debitBalanceIsFetching}
      creditBalanceIsLoading={creditBalanceIsLoading || creditBalanceIsFetching}
      onUpdateBalance={onUpdateBalance}
      debitAccountBalance={debitAccountBalance}
      creditAccountBalance={creditAccountBalance}
      debitBalances={debitBalances}
      creditBalances={creditBalances}
      allDebitAccountIsLoading={
        allDebitAccountBalancesIsLoading || allDebitAccountBalancesisFetching
      }
      allCreditAccountIsLoading={
        allCreditAccountBalancesIsLoading || allCreditAccountBalancesisFetching
      }
      isEditable
      onSearchAutocomplete={onSearch}
      errors={errors && Object.keys(errors).length ? errors : undefined}
      accountCategories={accountCategories}
      accountsCategoriesLoading={accountsCategoriesLoading}
      setCreditAccounts={setCreditAccounts}
      setCreditAccountQuery={getCreditAccountsListAction}
      currencyId={currencyId}
      errorSidepanel={globalError}
      sidepanelExpandedKey="customers-gl-fee"
    />
  );
};

export default TransactionFromFeeHOC;
